<template>
  <div>
    <div class="page-head">
      <div>
        <h1 class="page-head__title">{{ t("apps.title") }}</h1>
        <p class="page-head__lede">{{ t("apps.lede") }}</p>
      </div>
      <button class="btn" @click="creating = !creating">
        {{ creating ? t("common.cancel") : t("apps.create") }}
      </button>
    </div>

    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)">
      <div class="notice__body">{{ error }}</div>
    </div>

    <div v-if="creating" class="card" style="margin-bottom: var(--space-4)">
      <h2 class="card__title">{{ t("apps.createTitle") }}</h2>
      <div class="grid grid--2" style="margin-top: var(--space-4)">
        <div class="field">
          <label class="field__label" for="app-name">{{ t("apps.nameLabel") }}</label>
          <input id="app-name" v-model="draft.name" class="input" maxlength="120" />
          <span class="field__hint">{{ t("apps.nameHint") }}</span>
        </div>
        <div class="field">
          <label class="field__label" for="app-handle">{{ t("apps.handleLabel") }}</label>
          <input id="app-handle" v-model="draft.handle" class="input input--mono" maxlength="64" placeholder="fleet-cam" />
          <span class="field__hint">{{ t("apps.handleHint") }}</span>
        </div>
      </div>
      <button class="btn" :disabled="busy || !draft.name" @click="create">
        <span v-if="busy" class="spinner" aria-hidden="true"></span>
        {{ t("common.create") }}
      </button>
    </div>

    <div v-if="loading" class="card">
      <div class="row"><span class="spinner" aria-hidden="true"></span>
        <span class="muted small">{{ t("common.loading") }}</span></div>
    </div>

    <div v-else-if="!apps.length" class="card">
      <div class="empty">
        <p class="empty__title">{{ t("apps.emptyTitle") }}</p>
        <p class="small">{{ t("apps.emptyBody") }}</p>
      </div>
    </div>

    <div v-else class="grid grid--2">
      <router-link
        v-for="app in apps"
        :key="app.id"
        :to="{ name: 'app', params: { id: app.id } }"
        class="card app-card"
      >
        <div class="app-card__head">
          <span class="app-card__mark" aria-hidden="true">{{ mark(app.name) }}</span>
          <span :class="['badge', badgeClass(app.status)]">{{ statusLabel(app.status) }}</span>
        </div>
        <h3 class="app-card__name">{{ app.name }}</h3>
        <p class="app-card__handle mono xs muted">{{ app.handle }}</p>
        <p class="app-card__note xs muted">{{ statusNote(app.status) }}</p>
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { api } from "../lib/api";
import { useErrorText } from "../lib/errors";

const { t, te } = useI18n();
const errorText = useErrorText();
const router = useRouter();

const apps = ref([]);
const loading = ref(true);
const busy = ref(false);
const error = ref(null);
const creating = ref(false);
const draft = reactive({ name: "", handle: "" });

const BADGE = {
  published: "badge--success",
  private: "badge--brand",
  dev: "badge--brand",
  submitted: "badge--info",
  checking: "badge--info",
  checks_failed: "badge--danger",
  unpublished: "badge--warn",
  suspended: "badge--danger",
  blocked: "badge--danger"
};

function statusLabel(status) {
  return te(`apps.status.${status}`) ? t(`apps.status.${status}`) : status;
}
function statusNote(status) {
  return te(`apps.note.${status}`) ? t(`apps.note.${status}`) : "";
}
function badgeClass(status) {
  return BADGE[status] || "";
}
function mark(name) {
  return (name || "?").trim().slice(0, 1).toUpperCase();
}

async function load() {
  loading.value = true;
  try {
    apps.value = (await api.apps()) || [];
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}

async function create() {
  busy.value = true;
  error.value = null;
  try {
    const response = await api.createApp({
      name: draft.name,
      handle: draft.handle || undefined
    });
    router.push({ name: "app", params: { id: response.id } });
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.app-card {
  display: block;
  color: inherit;
  text-decoration: none;
  transition: border-color var(--transition), box-shadow var(--transition), transform var(--transition);
}
.app-card:hover {
  text-decoration: none;
  border-color: var(--brand-200);
  box-shadow: var(--shadow-lg);
  transform: translateY(-1px);
}
.app-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}
.app-card__mark {
  display: grid;
  place-items: center;
  width: 38px; height: 38px;
  border-radius: var(--radius);
  background: var(--accent-soft);
  color: var(--brand-700);
  font-weight: 600;
  font-size: var(--text-md);
}
.app-card__name { margin: 0 0 2px; font-size: var(--text-md); }
.app-card__handle { margin: 0 0 var(--space-3); }
.app-card__note { margin: 0; }
</style>
